import {
  type CreateProjectRequest,
  CSRF_HEADER,
  type GovernedProject,
  type ProjectView,
  type ProjectVisibility,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, FolderClosed, Lock, Plus } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { useBackofficeAccess } from "../admin/access.ts";
import { useAdminApi } from "../admin/admin-api.tsx";
import { useScopeEntities } from "../admin/reports/queries.ts";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { GOVERNED_PROJECTS_PATH, Link, navigate, projectHomePath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { projectKeys, useProjects } from "./queries.ts";

/** The projects the user can see, and creating one (`?new` opens the form, from home). */
export function ProjectList() {
  const { t } = useTranslation();
  const projects = useProjects();
  const [creating, setCreating] = useState(openedWithNew);
  const create = (
    <Button size="sm" onClick={() => setCreating(true)}>
      <Plus aria-hidden="true" strokeWidth={2} />
      {t("projects.create")}
    </Button>
  );

  return (
    <Page>
      <PageHeader title={t("projects.title")} lead={t("projects.lead")} actions={create} />
      <section data-esi-content="projects" aria-label={t("projects.list")}>
        {projects.isPending ? (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : projects.isError ? (
          <Notice tone="danger">{projects.error.message}</Notice>
        ) : projects.data.length === 0 ? (
          <EmptyState icon={FolderClosed} title={t("projects.create")} action={create}>
            {t("projects.empty")}
          </EmptyState>
        ) : (
          <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface-raised">
            {projects.data.map((p) => (
              <ProjectRow key={p.id} project={p} />
            ))}
          </ul>
        )}
      </section>
      {projects.data ? <GovernedElsewhere readable={projects.data} /> : null}
      <CreateProjectDialog open={creating} onOpenChange={setCreating} />
    </Page>
  );
}

/**
 * Projects the user governs as an entity or tenant admin but cannot open (spec `projects`
 * "Governed projects on the project list"): name and owning entity only, each leading to its
 * backoffice entry. Personal projects never appear; the section is absent for non-admins.
 */
function GovernedElsewhere({ readable }: { readable: readonly ProjectView[] }) {
  const access = useBackofficeAccess();
  return access === "yes" ? <GovernedList readable={readable} /> : null;
}

function GovernedList({ readable }: { readable: readonly ProjectView[] }) {
  const { t } = useTranslation();
  const { admin } = useAdminApi();
  // Shares the backoffice list's cache: same route, same scope.
  const governed = useQuery({
    queryKey: ["admin", "projects"],
    queryFn: async (): Promise<GovernedProject[]> => ok(await admin.projects.$get({ query: {} })),
    retry: false,
  });
  const entities = useScopeEntities();
  const open = new Set(readable.map((p) => p.id as string));
  const rows = (governed.data ?? []).filter(
    (p) => p.owner.kind === "entity" && p.name !== null && !open.has(p.id),
  );
  if (rows.length === 0) {
    return null;
  }
  const entityName = new Map((entities.data ?? []).map((e) => [e.id as string, e.name]));
  return (
    <section aria-labelledby="governed-elsewhere" className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2 id="governed-elsewhere" className="text-sm font-semibold text-fg">
          {t("governance.governed.title")}
        </h2>
        <p className="max-w-prose text-sm text-fg-secondary">{t("governance.governed.lead")}</p>
      </div>
      <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface-raised">
        {rows.map((p) => {
          const name = p.name ?? "";
          const owner =
            (p.owner.kind === "entity" && entityName.get(p.owner.entityId)) ||
            t("governance.governed.entityUnknown");
          return (
            <li key={p.id}>
              <Link
                href={`${GOVERNED_PROJECTS_PATH}/${p.id}`}
                aria-label={t("governance.governed.link", { name })}
                className="group flex items-center gap-3 px-4 py-3 transition-colors duration-(--duration-fast) outline-offset-[-2px] hover:bg-surface-hover"
              >
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-medium text-fg">{name}</span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
                    <Lock aria-hidden="true" className="size-3" strokeWidth={1.75} />
                    {owner}
                  </span>
                </span>
                <ChevronRight
                  aria-hidden="true"
                  className="size-4 shrink-0 text-fg-muted transition-transform duration-(--duration-fast) group-hover:translate-x-0.5"
                />
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** `?new` (home's "Create a project") opens the form when the page mounts. */
function openedWithNew(): boolean {
  const opened = new URLSearchParams(window.location.search).has("new");
  if (opened) {
    // Once: a reload or coming back must not reopen the form uninvited.
    window.history.replaceState(window.history.state, "", window.location.pathname);
  }
  return opened;
}

function ProjectRow({ project }: { project: ProjectView }) {
  const { t } = useTranslation();
  const meta = [
    project.owner.kind === "user"
      ? t("project.personal")
      : t(`project.visibility.${project.visibility}`),
    t(`projectRoles.${project.role}.name`),
  ];
  return (
    <li>
      <Link
        href={projectHomePath(project.id)}
        className="group flex items-center gap-3 px-4 py-3 transition-colors duration-(--duration-fast) outline-offset-[-2px] hover:bg-surface-hover"
      >
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-medium text-fg">{project.name}</span>
            {project.frozenAt ? (
              <Badge variant="outline" className="gap-1 text-fg-secondary">
                <Lock aria-hidden="true" />
                {t("project.frozenShort")}
              </Badge>
            ) : null}
          </span>
          {project.description ? (
            <span className="truncate text-sm text-fg-secondary">{project.description}</span>
          ) : null}
          <span className="text-xs text-fg-muted">{meta.join(" · ")}</span>
        </span>
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-fg-muted transition-transform duration-(--duration-fast) group-hover:translate-x-0.5"
        />
      </Link>
    </li>
  );
}

type Owner = "personal" | "entity";

function CreateProjectDialog(props: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("projects.create")}</DialogTitle>
          <DialogDescription>{t("projects.createLead")}</DialogDescription>
        </DialogHeader>
        {props.open ? <CreateProjectForm onCancel={() => props.onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function CreateProjectForm({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken, entityId } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [owner, setOwner] = useState<Owner>("personal");
  const [visibility, setVisibility] = useState<ProjectVisibility>("private");
  const create = useMutation({
    mutationFn: async (json: CreateProjectRequest) =>
      ok(await api.api.projects.$post({ json }, { headers: { [CSRF_HEADER]: csrfToken } })),
    onSuccess: (project) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
      navigate(projectHomePath(project.id));
    },
  });

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim() || create.isPending) {
          return;
        }
        create.mutate(
          owner === "personal"
            ? { name: name.trim() }
            : { name: name.trim(), ownerEntityId: entityId, visibility },
        );
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-name`}>{t("projects.name")}</Label>
        <Input
          id={`${id}-name`}
          value={name}
          maxLength={200}
          required
          autoFocus
          onChange={(e) => setName(e.currentTarget.value)}
        />
      </div>
      <fieldset className="flex flex-col gap-2.5">
        <legend className="mb-2 text-sm font-medium text-fg">{t("projects.owner")}</legend>
        <RadioGroup value={owner} onValueChange={(v) => setOwner(v as Owner)} className="gap-2.5">
          {(["personal", "entity"] as const).map((o) => (
            <div key={o} className="flex items-start gap-2.5">
              <RadioGroupItem
                id={`${id}-owner-${o}`}
                value={o}
                aria-describedby={`${id}-owner-${o}-hint`}
                className="mt-0.5"
              />
              <div className="flex flex-col gap-0.5">
                <Label htmlFor={`${id}-owner-${o}`}>{t(`projects.ownerLabel.${o}`)}</Label>
                <p id={`${id}-owner-${o}-hint`} className="text-sm text-fg-secondary">
                  {t(`projects.ownerHint.${o}`)}
                </p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </fieldset>
      {owner === "entity" ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-visibility`}>{t("projects.visibilityLabel")}</Label>
          <NativeSelect
            id={`${id}-visibility`}
            value={visibility}
            onChange={(e) => setVisibility(e.currentTarget.value as ProjectVisibility)}
          >
            {(["private", "entity", "entity_tree"] as const).map((v) => (
              <option key={v} value={v}>
                {t(`project.visibility.${v}`)}
              </option>
            ))}
          </NativeSelect>
        </div>
      ) : null}
      {create.isError ? <Notice tone="danger">{create.error.message}</Notice> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("app.cancel")}
        </Button>
        <Button type="submit" disabled={!name.trim() || create.isPending}>
          {create.isPending ? t("projects.creating") : t("projects.createSubmit")}
        </Button>
      </DialogFooter>
    </form>
  );
}
